import React from "react";
import "./Receipt.css";
import { Box, Typography, Divider, Paper, Button } from "@mui/material";
import { Print as PrintIcon, Error as ErrorIcon, CheckCircle as SuccessIcon } from "@mui/icons-material";
import successImage from "../../assets/payment-success.png";

const Receipt = ({ paymentData, customerInfo, billingData }) => {
  const currentDate = new Date().toLocaleString();
  
  const handlePrint = () => {
    window.print();
  };
  
  // Determine if transaction was successful
  // A transaction is only successful if ALL of these conditions are true:
  // 1. response_code is exactly 101 (Transaction approved)
  // 2. approval_code exists and is not empty
  // 3. response_message does not contain failure keywords
  const isSuccess =
    paymentData?.response_code === 101 &&
    paymentData?.approval_code &&
    paymentData.approval_code.trim() !== "" &&
    (!paymentData?.response_message ||
     (!paymentData.response_message.toLowerCase().includes("not approved") &&
      !paymentData.response_message.toLowerCase().includes("declined")));

  // Rejected before the request ever reached PayTrace (amount validation, the
  // card-testing guard, a reCAPTCHA step-up). There is no transaction to report
  // on - only the reason - so the gateway rows are hidden and the reason shown.
  const blockedMessage = paymentData?.error_message;

  return (
    <Paper className="receipt-container" elevation={3}>
      <Box className="receipt-header">
        {isSuccess ? (
          <>
            <img src={successImage} alt="Payment Success" className="receipt-success-icon" />
            <Typography variant="h4" className="receipt-title">
              Payment Receipt
            </Typography>
            <Typography variant="h6" className="receipt-status success" style={{ color: "#4caf50" }}>
              <SuccessIcon style={{ marginRight: "8px", verticalAlign: "middle" }} />
              Transaction Successful
            </Typography>
          </>
        ) : (
          <>
            <ErrorIcon style={{ fontSize: "80px", color: "#f44336", marginBottom: "10px" }} />
            <Typography variant="h4" className="receipt-title">
              Payment Receipt
            </Typography>
            <Typography variant="h6" className="receipt-status error" style={{ color: "#f44336" }}>
              <ErrorIcon style={{ marginRight: "8px", verticalAlign: "middle" }} />
              Transaction Failed
            </Typography>
          </>
        )}
      </Box>

      <Divider sx={{ margin: "20px 0" }} />

      {blockedMessage && (
        <Box className="receipt-section">
          <Typography
            className="receipt-blocked-message"
            style={{ color: "#f44336", fontWeight: 500, textAlign: "center" }}
          >
            {blockedMessage}
          </Typography>
        </Box>
      )}

      {/* Transaction Details */}
      <Box className="receipt-section">
        <Typography variant="h6" className="section-title">
          Transaction Details
        </Typography>
        {!blockedMessage && (
          <>
            <Box className="receipt-row">
              <Typography className="receipt-label">Transaction ID:</Typography>
              <Typography className="receipt-value">{paymentData?.transaction_id}</Typography>
            </Box>
            <Box className="receipt-row">
              <Typography className="receipt-label">Transaction UUID:</Typography>
              <Typography className="receipt-value">{paymentData?.transaction_uuid}</Typography>
            </Box>
            <Box className="receipt-row">
              <Typography className="receipt-label">Amount Paid:</Typography>
              <Typography className="receipt-value receipt-amount">${paymentData?.total_amount}</Typography>
            </Box>
            <Box className="receipt-row">
              <Typography className="receipt-label">Response Code:</Typography>
              <Typography className="receipt-value">{paymentData?.response_code}</Typography>
            </Box>
          </>
        )}
        <Box className="receipt-row">
          <Typography className="receipt-label">Status:</Typography>
          <Typography className="receipt-value" style={{ color: isSuccess ? "#4caf50" : "#f44336" }}>
            {paymentData?.response_message}
          </Typography>
        </Box>
        {isSuccess && paymentData?.approval_code && (
          <Box className="receipt-row">
            <Typography className="receipt-label">Approval Code:</Typography>
            <Typography className="receipt-value">{paymentData?.approval_code}</Typography>
          </Box>
        )}
        {!isSuccess && paymentData?.approval_message && (
          <Box className="receipt-row">
            <Typography className="receipt-label">Error Details:</Typography>
            <Typography className="receipt-value" style={{ color: "#f44336" }}>
              {paymentData?.approval_message}
            </Typography>
          </Box>
        )}
        <Box className="receipt-row">
          <Typography className="receipt-label">Date & Time:</Typography>
          <Typography className="receipt-value">{currentDate}</Typography>
        </Box>
      </Box>

      <Divider sx={{ margin: "20px 0" }} />

      {/* Payer Information */}
      <Box className="receipt-section">
        <Typography variant="h6" className="section-title">
          Payer Information
        </Typography>
        <Box className="receipt-row">
          <Typography className="receipt-label">Name:</Typography>
          <Typography className="receipt-value">{customerInfo?.name}</Typography>
        </Box>
        <Box className="receipt-row">
          <Typography className="receipt-label">Email:</Typography>
          <Typography className="receipt-value">{customerInfo?.email}</Typography>
        </Box>
        {customerInfo?.phone && (
          <Box className="receipt-row">
            <Typography className="receipt-label">Phone:</Typography>
            <Typography className="receipt-value">{customerInfo?.phone}</Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ margin: "20px 0" }} />

      {/* Footer */}
      <Box className="receipt-footer">
        {isSuccess ? (
          <>
            <Typography variant="body2" className="receipt-footer-text">
              Thank you for your payment. Please keep this receipt for your records.
            </Typography>
            <Typography variant="body2" className="receipt-footer-text">
              If you have any questions about this transaction, please contact us with your Transaction ID.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2" className="receipt-footer-text" style={{ color: "#f44336" }}>
              Your payment could not be processed. Please keep this receipt for your records.
            </Typography>
            <Typography variant="body2" className="receipt-footer-text">
              If you have any questions about this failed transaction, please contact us with your Transaction ID.
            </Typography>
            <Typography variant="body2" className="receipt-footer-text" style={{ fontWeight: "bold" }}>
              Please try again with a different payment method or contact your bank for assistance.
            </Typography>
          </>
        )}
        <Box className="receipt-print-section">
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              marginTop: "15px",
              color: "#305496",
              borderColor: "#305496",
              "&:hover": {
                borderColor: "#264075",
                backgroundColor: "#f5f5f5"
              }
            }}
          >
            Print Receipt
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default Receipt; 